import { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import instanciaAxios from '../miscelaneo/instanciaAxios';
import { roundTo2 } from '../miscelaneo/monetario';

/**
 * Tasa de ITBIS efectiva de un producto para fines de cálculo/edición de
 * precio: la misma que aplicará calcularImpuestoPorProducto al registrar la
 * compra (0 si el indicador de facturación no es 1/2/3, aunque el producto
 * tenga itbis_tasa cargado).
 */
function obtenerTasaItbisEfectiva(producto) {
  if (!producto) return 0;
  const indicador = producto.indicador_facturacion || 1;
  if (![1, 2, 3].includes(indicador)) return 0;

  let tasa = indicador === 2 ? 16 : 18;
  if (producto.itbis_tasa !== undefined && producto.itbis_tasa !== null) {
    tasa = producto.itbis_tasa;
  }
  return tasa;
}

/**
 * Hook compartido para gestión de tickets de compra.
 * Sigue el mismo patrón de useTicketManager pero adaptado para compras.
 * 
 * Diferencias principales con useTicketManager:
 * - Usa proveedores en lugar de clientes
 * - NCF viene del proveedor (no se genera)
 * - Incluye campo es_informal para compras informales
 * - No maneja tipos de comprobante fiscal propios
 * 
 * @returns {Object} Estado y funciones de gestión de tickets de compra
 */
export default function useCompraTicketManager() {
  // Estado principal
  const [tickets, setTickets] = useState([
    {
      id: 1,
      nombre: 'Compra 1',
      productos: [],
      totales: { subtotal: 0, itbis: 0, total: 0 },
      proveedorSeleccionado: null,
      nombreProveedor: '',
      ncf: '',
      es_informal: false,
      comentario: '',
      id_centro_costo: null,
      tipoPago: 'contado',
      metodosPago: [{ metodoPago: '01', monto: '' }],
      abonoInicial: null,
      // Edit mode fields
      id_compra_editando: null,
      productosOriginales: null
    }
  ]);
  const [ticketActivo, setTicketActivo] = useState(0);
  const [sucursales, setSucursales] = useState([]);
  const [sucursalSeleccionada, setSucursalSeleccionada] = useState('');
  const [centrosCosto, setCentrosCosto] = useState([]);
  const [mostrarCamposAdicionales, setMostrarCamposAdicionales] = useState(false);
  const [mostrarModalProveedores, setMostrarModalProveedores] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [procesandoCobro, setProcesandoCobro] = useState(false);
  
  // Modal para cantidad de producto
  const [mostrarModalCantidad, setMostrarModalCantidad] = useState(false);
  const [productoTemporal, setProductoTemporal] = useState(null);
  const [cantidadTemporal, setCantidadTemporal] = useState('1');
  const [precioTemporal, setPrecioTemporal] = useState('');
  const [modoModal, setModoModal] = useState('agregar');
  // Modo de edición de precio dentro del modal de producto: 'unitario' edita
  // el costo base directamente (comportamiento histórico); 'total' permite
  // digitar el total con ITBIS incluido y calcula el costo base a partir de
  // ese total (mismo enfoque de MantenimientoProducto: total / (1 + tasa/100)).
  const [modoPrecioTemporal, setModoPrecioTemporal] = useState('unitario');
  const [totalTemporalConImpuesto, setTotalTemporalConImpuesto] = useState('');
  const [indexProductoEditar, setIndexProductoEditar] = useState(null);
  
  // Modal de pago
  const [mostrarModalPago, setMostrarModalPago] = useState(false);
  
  // Estados para PDF y compra creada
  const [compraCreada, setCompraCreada] = useState(null);
  const [mostrarPDFCompra, setMostrarPDFCompra] = useState(false);
  const [pdfBase64, setPdfBase64] = useState(null);
  const [idCompraPDF, setIdCompraPDF] = useState(null);
  
  // Métodos de pago (catálogo)
  const [metodosPago, setMetodosPago] = useState([]);

  // Defaults del sistema
  const [sucursalDefecto, setSucursalDefecto] = useState(null);

  // Búsqueda de compras
  const [mostrarModalBusquedaCompras, setMostrarModalBusquedaCompras] = useState(false);
  const [comprasBuscadas, setComprasBuscadas] = useState([]);
  const [cargandoCompras, setCargandoCompras] = useState(false);
  const [filtrosBusqueda, setFiltrosBusqueda] = useState({
    busqueda: '',
    fecha_desde: '',
    fecha_hasta: ''
  });

  // Referencias
  const inputProductoRef = useRef();
  const inputCantidadRef = useRef();

  // Cargar datos iniciales
  useEffect(() => {
    const fetchData = async () => {
      try {
        setCargando(true);
        const [sucursalesResp, centrosCostoResp, metodosPagoResp, configuracionResp] = await Promise.all([
          instanciaAxios.get('/miscelaneo/sucursal'),
          instanciaAxios.get('/miscelaneo/centro_costo'),
          instanciaAxios.get('/miscelaneo/metodo_pago'),
          instanciaAxios.get('/miscelaneo/configuracion').catch(() => ({ data: {} }))
        ]);
        setSucursales(sucursalesResp.data);
        setCentrosCosto(centrosCostoResp.data);
        setMetodosPago(metodosPagoResp.data);

        const configData = configuracionResp.data;
        if (configData?.id_sucursal_defecto) {
          setSucursalDefecto(configData.id_sucursal_defecto);
          setSucursalSeleccionada(String(configData.id_sucursal_defecto));
        }
      } catch (error) {
        console.error('Error al cargar datos iniciales:', error);
        toast.error('Error al cargar datos iniciales');
      } finally {
        setCargando(false);
      }
    };
    fetchData();
  }, []);

  // Atajos de teclado para cambiar tickets
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key >= '1' && e.key <= '9') {
        const index = parseInt(e.key) - 1;
        if (index < tickets.length) {
          setTicketActivo(index);
          e.preventDefault();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [tickets]);

  // Focus automático en input de producto al cambiar ticket
  useEffect(() => {
    if (inputProductoRef.current) {
      setTimeout(() => {
        inputProductoRef.current?.limpiar?.();
      }, 100);
    }
  }, [ticketActivo]);

  // === TICKETS ===
  const crearNuevoTicket = () => {
    if (tickets.length >= 10) {
      toast.warning('Máximo 10 tickets simultáneos');
      return;
    }
    
    const nuevoId = Math.max(...tickets.map(t => t.id)) + 1;
    const nuevoTicket = {
      id: nuevoId,
      nombre: `Compra ${nuevoId}`,
      productos: [],
      totales: { subtotal: 0, itbis: 0, total: 0 },
      proveedorSeleccionado: null,
      nombreProveedor: '',
      ncf: '',
      es_informal: false,
      comentario: '',
      id_centro_costo: null,
      tipoPago: 'contado',
      metodosPago: [{ metodoPago: '01', monto: '' }],
      abonoInicial: null,
      id_compra_editando: null,
      productosOriginales: null
    };
    
    setTickets([...tickets, nuevoTicket]);
    setTicketActivo(tickets.length);
    toast.success(`${nuevoTicket.nombre} creado`);
  };

  const cerrarTicket = (index) => {
    const ticket = tickets[index];
    
    if (ticket.productos.length > 0) {
      if (!window.confirm(`${ticket.nombre} tiene productos. ¿Seguro que deseas cerrarlo?`)) {
        return;
      }
    }
    
    if (tickets.length === 1) {
      setTickets([{
        ...ticket,
        productos: [],
        totales: { subtotal: 0, itbis: 0, total: 0 },
        proveedorSeleccionado: null,
        nombreProveedor: '',
        ncf: '',
        es_informal: false,
        comentario: '',
        id_centro_costo: null,
        tipoPago: 'contado',
        metodosPago: [{ metodoPago: '01', monto: '' }],
        abonoInicial: null,
        id_compra_editando: null,
        productosOriginales: null
      }]);
      toast.info('Ticket limpiado');
      return;
    }
    
    const nuevosTickets = tickets.filter((_, i) => i !== index);
    setTickets(nuevosTickets);
    
    if (ticketActivo >= nuevosTickets.length) {
      setTicketActivo(nuevosTickets.length - 1);
    }
    
    toast.success(`${ticket.nombre} cerrado`);
  };

  const limpiarTicketActivo = () => {
    const ticket = tickets[ticketActivo];
    const nuevosTickets = [...tickets];
    nuevosTickets[ticketActivo] = {
      ...ticket,
      productos: [],
      totales: { subtotal: 0, itbis: 0, total: 0 },
      proveedorSeleccionado: null,
      nombreProveedor: '',
      ncf: '',
      es_informal: false,
      comentario: '',
      id_centro_costo: null,
      tipoPago: 'contado',
      metodosPago: [{ metodoPago: '01', monto: '' }],
      abonoInicial: null,
      id_compra_editando: null,
      productosOriginales: null
    };
    setTickets(nuevosTickets);
  };

  // === PROVEEDORES ===
  const handleSeleccionarProveedor = (proveedor) => {
    const nuevosTickets = [...tickets];
    nuevosTickets[ticketActivo].proveedorSeleccionado = proveedor.id_entidad;
    nuevosTickets[ticketActivo].nombreProveedor = 
      proveedor.razon_social || `${proveedor.nombre} ${proveedor.apellido || ''}`.trim();
    setTickets(nuevosTickets);
    setMostrarModalProveedores(false);
  };

  const handleClickBuscarProveedor = () => {
    setMostrarModalProveedores(true);
  };

  // === PRODUCTOS ===
  const handleProductoSeleccionado = (producto) => {
    if (!producto) return;

    setProductoTemporal(producto);
    setCantidadTemporal('1');
    // For purchases, use costo (cost) if available, else precio
    const precioCosto = parseFloat(producto.costo || producto.precio || 0);
    setPrecioTemporal(precioCosto.toString());
    setModoModal('agregar');
    setIndexProductoEditar(null);
    setModoPrecioTemporal('unitario');
    setTotalTemporalConImpuesto('');

    requestAnimationFrame(() => {
      setMostrarModalCantidad(true);
    });
  };

  const handleClickInputConProducto = (producto) => {
    if (producto && productoTemporal) {
      setMostrarModalCantidad(true);
    }
  };

  // Recalcula precioTemporal (base) a partir de un total con ITBIS incluido
  // para la cantidad actual del modal.
  const actualizarTotalTemporal = (valorTotal) => {
    setTotalTemporalConImpuesto(valorTotal);

    const cantidad = parseFloat(cantidadTemporal) || 0;
    const total = parseFloat(valorTotal);
    if (cantidad <= 0 || !Number.isFinite(total)) return;

    const tasa = obtenerTasaItbisEfectiva(productoTemporal);
    const precioBase = roundTo2(total / cantidad / (1 + tasa / 100));
    setPrecioTemporal(precioBase.toString());
  };

  // Si el usuario cambia la cantidad mientras está en modo "Total", el costo
  // base debe recalcularse para que el total con impuesto se mantenga fijo.
  useEffect(() => {
    if (modoPrecioTemporal !== 'total') return;
    if (totalTemporalConImpuesto === '') return;

    const cantidad = parseFloat(cantidadTemporal) || 0;
    const total = parseFloat(totalTemporalConImpuesto);
    if (cantidad <= 0 || !Number.isFinite(total)) return;

    const tasa = obtenerTasaItbisEfectiva(productoTemporal);
    const precioBase = roundTo2(total / cantidad / (1 + tasa / 100));
    setPrecioTemporal(precioBase.toString());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cantidadTemporal]);

  const agregarProductoAlTicket = () => {
    if (!productoTemporal) return;
    
    const cantidad = parseFloat(cantidadTemporal) || 0;
    if (cantidad <= 0 || cantidadTemporal === '') {
      toast.error('La cantidad debe ser mayor a 0');
      return;
    }

    const precioEditado = parseFloat(precioTemporal);
    if (isNaN(precioEditado) || precioEditado < 0) {
      toast.error('El precio debe ser un número válido');
      return;
    }
    
    const nuevosTickets = [...tickets];
    const ticket = nuevosTickets[ticketActivo];

    if (modoModal === 'editar') {
      if (indexProductoEditar !== null) {
        ticket.productos[indexProductoEditar].cantidad = cantidad;
        ticket.productos[indexProductoEditar].precio = precioEditado;
        toast.success('Producto actualizado');
      }
    } else {
      const indexExistente = ticket.productos.findIndex(
        p => p.id_producto === productoTemporal.id_producto
      );
      
      if (indexExistente >= 0) {
        ticket.productos[indexExistente].cantidad += cantidad;
        toast.success('Cantidad actualizada');
      } else {
        const indicador = productoTemporal.indicador_facturacion || 1;
        
        let tasaITBIS = 18;
        if (indicador === 2) tasaITBIS = 16;
        else if ([3, 4, 5].includes(indicador)) tasaITBIS = 0;
        
        if (productoTemporal.itbis_tasa !== undefined && productoTemporal.itbis_tasa !== null) {
          tasaITBIS = productoTemporal.itbis_tasa;
        }
        
        const nuevoProducto = {
          id_producto: productoTemporal.id_producto,
          descripcion: productoTemporal.descripcion,
          precio: precioEditado,
          cantidad: cantidad,
          indicador_facturacion: indicador,
          itbis_tasa: tasaITBIS,
          id_tipo_producto: productoTemporal.id_tipo_producto || 1
        };
        ticket.productos.push(nuevoProducto);
        toast.success('Producto agregado');
      }
    }
    
    calcularTotales(ticket);
    setTickets(nuevosTickets);
    
    setMostrarModalCantidad(false);
    setProductoTemporal(null);
    setCantidadTemporal('1');
    setPrecioTemporal('');
    setModoModal('agregar');
    setIndexProductoEditar(null);
    setModoPrecioTemporal('unitario');
    setTotalTemporalConImpuesto('');

    setTimeout(() => {
      inputProductoRef.current?.limpiarYFocus?.();
    }, 100);
  };

  const eliminarProducto = (indexProducto) => {
    const nuevosTickets = [...tickets];
    const ticket = nuevosTickets[ticketActivo];
    
    ticket.productos.splice(indexProducto, 1);
    calcularTotales(ticket);
    setTickets(nuevosTickets);
    
    toast.info('Producto eliminado');
  };

  const editarCantidadProducto = (indexProducto) => {
    const ticket = tickets[ticketActivo];
    const producto = ticket.productos[indexProducto];
    
    setProductoTemporal(producto);
    setCantidadTemporal(producto.cantidad.toString());
    setPrecioTemporal(parseFloat(producto.precio || 0).toString());
    setModoModal('editar');
    setIndexProductoEditar(indexProducto);
    setModoPrecioTemporal('unitario');
    setTotalTemporalConImpuesto('');
    setMostrarModalCantidad(true);
  };

  // Agregar producto rápido (barcode scan / Enter en referencia): sin modal.
  // Para productos pesables con peso embebido en el barcode, usa ese peso como cantidad.
  const agregarProductoRapido = (producto) => {
    if (!producto) return;

    const nuevosTickets = [...tickets];
    const ticket = nuevosTickets[ticketActivo];

    const esPesableConPeso = producto.es_pesable && producto.peso_embebido > 0;
    const cantidadNueva = esPesableConPeso ? producto.peso_embebido : 1;

    const indexExistente = ticket.productos.findIndex(
      p => p.id_producto === producto.id_producto
    );

    if (indexExistente >= 0) {
      if (esPesableConPeso) {
        ticket.productos[indexExistente].cantidad = roundTo2(
          ticket.productos[indexExistente].cantidad + producto.peso_embebido
        );
        toast.success(`+${producto.peso_embebido}kg ${producto.descripcion}`);
      } else {
        ticket.productos[indexExistente].cantidad += 1;
        toast.success(`+1 ${producto.descripcion}`);
      }
    } else {
      const indicador = producto.indicador_facturacion || 1;
      let tasaITBIS = 18;
      if (indicador === 2) tasaITBIS = 16;
      else if ([3, 4, 5].includes(indicador)) tasaITBIS = 0;
      if (producto.itbis_tasa !== undefined && producto.itbis_tasa !== null) {
        tasaITBIS = producto.itbis_tasa;
      }

      // For purchases, use costo if available
      const precioCosto = parseFloat(producto.costo || producto.precio || 0);

      ticket.productos.push({
        id_producto: producto.id_producto,
        descripcion: producto.descripcion,
        precio: precioCosto,
        cantidad: cantidadNueva,
        indicador_facturacion: indicador,
        itbis_tasa: tasaITBIS,
        id_tipo_producto: producto.id_tipo_producto || 1
      });
      const mensajeAgregado = esPesableConPeso
        ? `${producto.descripcion} (${producto.peso_embebido}kg) agregado`
        : `${producto.descripcion} agregado`;
      toast.success(mensajeAgregado);
    }

    calcularTotales(ticket);
    setTickets(nuevosTickets);
  };

  // === CÁLCULOS ===
  const calcularImpuestoPorProducto = (producto) => {
    const subtotal = roundTo2(producto.precio * producto.cantidad);
    if ([1, 2, 3].includes(producto.indicador_facturacion)) {
      return roundTo2(subtotal * (producto.itbis_tasa / 100));
    }
    return 0;
  };

  const calcularTotales = (ticket) => {
    const subtotal = ticket.productos.reduce(
      (acc, p) => acc + roundTo2(p.precio * p.cantidad),
      0
    );
    const itbis = ticket.productos.reduce(
      (acc, p) => acc + calcularImpuestoPorProducto(p),
      0
    );
    ticket.totales = {
      subtotal: roundTo2(subtotal),
      itbis: roundTo2(itbis),
      total: roundTo2(subtotal + itbis)
    };
  };

  // === BÚSQUEDA DE COMPRAS ===
  const buscarCompras = async (filtrosActuales) => {
    const filtros = filtrosActuales || filtrosBusqueda;
    try {
      setCargandoCompras(true);
      const params = { limite: 50 };
      if (filtros.busqueda) params.busqueda = filtros.busqueda;
      if (filtros.fecha_desde) params.fecha_desde = filtros.fecha_desde;
      if (filtros.fecha_hasta) params.fecha_hasta = filtros.fecha_hasta;

      const response = await instanciaAxios.get('/cxp', { params });
      setComprasBuscadas(response.data?.compras || []);
    } catch (error) {
      console.error('Error al buscar compras:', error);
      toast.error('Error al buscar compras');
    } finally {
      setCargandoCompras(false);
    }
  };

  const handleBotonBuscar = () => {
    setMostrarModalBusquedaCompras(true);
    buscarCompras();
  };

  const limpiarFiltrosBusqueda = () => {
    setFiltrosBusqueda({
      busqueda: '',
      fecha_desde: '',
      fecha_hasta: ''
    });
  };

  // Ticket activo helper
  const ticketActual = tickets[ticketActivo] || tickets[0];

  return {
    // State
    tickets, setTickets,
    ticketActivo, setTicketActivo,
    ticketActual,
    sucursales, setSucursales,
    sucursalSeleccionada, setSucursalSeleccionada,
    centrosCosto,
    mostrarCamposAdicionales, setMostrarCamposAdicionales,
    mostrarModalProveedores, setMostrarModalProveedores,
    cargando,
    procesandoCobro, setProcesandoCobro,
    mostrarModalCantidad, setMostrarModalCantidad,
    productoTemporal, setProductoTemporal,
    cantidadTemporal, setCantidadTemporal,
    precioTemporal, setPrecioTemporal,
    modoModal, setModoModal,
    modoPrecioTemporal, setModoPrecioTemporal,
    totalTemporalConImpuesto, actualizarTotalTemporal,
    indexProductoEditar, setIndexProductoEditar,
    mostrarModalPago, setMostrarModalPago,
    compraCreada, setCompraCreada,
    mostrarPDFCompra, setMostrarPDFCompra,
    pdfBase64, setPdfBase64,
    idCompraPDF, setIdCompraPDF,
    metodosPago,
    sucursalDefecto,
    mostrarModalBusquedaCompras, setMostrarModalBusquedaCompras,
    comprasBuscadas,
    cargandoCompras,
    filtrosBusqueda, setFiltrosBusqueda,
    inputProductoRef,
    inputCantidadRef,
    // Ticket functions
    crearNuevoTicket,
    cerrarTicket,
    limpiarTicketActivo,
    // Provider functions
    handleSeleccionarProveedor,
    handleClickBuscarProveedor,
    // Product functions
    handleProductoSeleccionado,
    handleClickInputConProducto,
    agregarProductoAlTicket,
    agregarProductoRapido,
    eliminarProducto,
    editarCantidadProducto,
    // Calculation functions
    calcularImpuestoPorProducto,
    calcularTotales,
    obtenerTasaItbisEfectiva,
    // Search functions
    buscarCompras,
    handleBotonBuscar,
    limpiarFiltrosBusqueda
  };
}

